import React, { useState } from 'react';
import { Menu, MenuItem } from '@mui/material';
import Swal from 'sweetalert2';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { useGetUserData } from '@hooks/useGetUserData';
import { iconButtonHoverRaiseStyle } from '@utils/styles';
import { CustomBlogPost } from '@api/database/models/customBlogPost';
import {
  useAdminGetCustomBlogPost,
  useDeleteCustomBlogPost
} from '@hooks/useCustomBlogPosts';
import { copyTextToClipboard } from '@utils/index';
import { getCustomBlogPostUrl } from '@utils/pageDealUtils';
import { useSnackbar } from '@hooks/useSnackbar';
import CreateEditCustomBlogPost from '@pages/Sales/components/CustomBlogPosts/components/CreateEditCustomBlogPost';

const AdminBlogPostActions = ({
  customBlogPost
}: {
  customBlogPost: CustomBlogPost | null;
}) => {
  const [editOpen, setEditOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { data: user } = useGetUserData();
  const { showMessage } = useSnackbar();
  const { mutate: deleteCustomBlogPost } = useDeleteCustomBlogPost();

  if (!user?.isAdmin || !customBlogPost) {
    return null;
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCopyBlogLink = () => {
    copyTextToClipboard(getBlogPostText());
    showMessage('Copied link to clipboard');
  };

  const getBlogPostText = () => {
    return `${customBlogPost.title}
${getCustomBlogPostUrl(customBlogPost?.slug)}`;
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      {editOpen ? (
        <CreateEditCustomBlogPost
          id={customBlogPost.id}
          onClose={() => {
            setEditOpen(false);
            handleClose();
          }}
        />
      ) : null}
      <IconButton
        id="admin-blog-post-button"
        aria-label="Admin page options"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={(e) => {
          e.preventDefault();
          handleClick(e);
        }}
        size="small"
        sx={iconButtonHoverRaiseStyle}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button'
        }}
      >
        <MenuItem
          key="edit-blog-post"
          onClick={() => {
            setEditOpen(true);
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          key="copy-blog-link"
          onClick={() => {
            handleCopyBlogLink();
            handleClose();
          }}
        >
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy Blog Link</ListItemText>
        </MenuItem>
        <MenuItem
          key="delete-blog-post"
          onClick={() => {
            // handleCopyLink();
            Swal.fire({
              title: 'Are you sure you want to delete this blog post?',
              showCancelButton: true,
              confirmButtonText: 'Delete',
              denyButtonText: `Don't delete`
            }).then((result) => {
              if (result.isConfirmed) {
                deleteCustomBlogPost({ id: customBlogPost.id });
                handleClose();
              } else {
                handleClose();
              }
            });
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

const AdminBlogActionsBySlugChild = ({ slug }: { slug: string }) => {
  const { data: customBlogPost } = useAdminGetCustomBlogPost(slug);

  if (!customBlogPost) {
    return null;
  }

  return <AdminBlogPostActions customBlogPost={customBlogPost} />;
};

export const AdminBlogActionsBySlug = ({ slug }: { slug: string }) => {
  const { data: user } = useGetUserData();

  if (!user?.isAdmin) {
    return null;
  }

  return <AdminBlogActionsBySlugChild slug={slug} />;
};

export default AdminBlogPostActions;
